import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import { 
  Percent, 
  Save, 
  RotateCcw,
  Loader2,
  Plus,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Info
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AccountDiscountSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    tiers: [
      { min_days: 0, max_days: 7, percentage: 2.5, label: "Within 7 days" },
      { min_days: 8, max_days: 14, percentage: 1.5, label: "Within 14 days" },
      { min_days: 15, max_days: 30, percentage: 0.5, label: "Within 30 days" },
      { min_days: 31, max_days: null, percentage: -1.5, label: "Over 30 days (monthly penalty)" }
    ],
    late_penalty_recurring: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/account-discounts`);
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching account discount settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate tiers
    for (let i = 0; i < settings.tiers.length; i++) {
      const tier = settings.tiers[i];
      if (tier.label.trim() === '') {
        toast.error(`Tier ${i + 1}: Label is required`);
        return;
      }
      if (tier.min_days < 0) {
        toast.error(`Tier ${i + 1}: Minimum days cannot be negative`);
        return;
      }
      if (tier.max_days !== null && tier.max_days < tier.min_days) {
        toast.error(`Tier ${i + 1}: Maximum days must be greater than minimum days`);
        return;
      }
    }

    setSaving(true);
    try {
      await saveWithRetry(() => axios.put(`${API}/settings/account-discounts`, settings));
      toast.success("Account discount settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(errorMessage(error, "Failed to save settings"));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm("Reset to default discount settings?")) {
      setSettings({
        enabled: true,
        tiers: [
          { min_days: 0, max_days: 7, percentage: 2.5, label: "Within 7 days" },
          { min_days: 8, max_days: 14, percentage: 1.5, label: "Within 14 days" },
          { min_days: 15, max_days: 30, percentage: 0.5, label: "Within 30 days" },
          { min_days: 31, max_days: null, percentage: -1.5, label: "Over 30 days (monthly penalty)" }
        ],
        late_penalty_recurring: true
      });
      toast.info("Settings reset to defaults (not saved yet)");
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...settings.tiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setSettings({ ...settings, tiers: newTiers });
  };

  const addTier = () => {
    const lastTier = settings.tiers[settings.tiers.length - 1];
    const newMinDays = lastTier?.max_days ? lastTier.max_days + 1 : 0;
    
    setSettings({
      ...settings,
      tiers: [
        ...settings.tiers,
        { min_days: newMinDays, max_days: null, percentage: 0, label: "New tier" }
      ]
    });
  };

  const removeTier = (index) => {
    if (settings.tiers.length <= 1) {
      toast.error("At least one tier is required");
      return;
    }
    const newTiers = settings.tiers.filter((_, i) => i !== index);
    setSettings({ ...settings, tiers: newTiers });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl" data-testid="account-discount-settings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Percent className="h-6 w-6" />
            Account Discount Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure early payment discounts and late payment penalties for account customers
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-discount-settings">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Enable Account Discounts</Label>
              <p className="text-sm text-muted-foreground">
                Automatically apply early payment discounts and late payment penalties when recording payments
              </p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              data-testid="enable-discounts-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="mb-6 border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How it works:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>When a payment is recorded, the system checks the age of the oldest unpaid invoice</li>
                <li><strong>Positive percentages</strong> = discount (reduces amount owed)</li>
                <li><strong>Negative percentages</strong> = penalty (increases amount owed)</li>
                <li>Discounts/penalties are automatically applied as separate journal entries</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Discount Tiers */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Payment Timing Tiers
          </CardTitle>
          <CardDescription>
            Define discount or penalty percentages based on how quickly payment is received
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {settings.tiers.map((tier, index) => (
              <div 
                key={index} 
                className={`p-4 rounded-lg border ${tier.percentage >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                data-testid={`discount-tier-${index}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {tier.percentage >= 0 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                    )}
                    <span className={`font-medium ${tier.percentage >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {tier.percentage >= 0 ? 'Discount' : 'Penalty'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeTier(index)}
                    className="text-gray-500 hover:text-red-600"
                    data-testid={`remove-tier-${index}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-gray-600">Label</Label>
                    <Input
                      value={tier.label}
                      onChange={(e) => updateTier(index, 'label', e.target.value)}
                      placeholder="e.g., Within 7 days"
                      className="mt-1"
                      data-testid={`tier-label-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">From Day</Label>
                    <Input
                      type="number"
                      min="0"
                      value={tier.min_days}
                      onChange={(e) => updateTier(index, 'min_days', parseInt(e.target.value) || 0)}
                      className="mt-1"
                      data-testid={`tier-min-days-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">To Day (empty = no limit)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={tier.max_days === null ? '' : tier.max_days}
                      onChange={(e) => updateTier(index, 'max_days', e.target.value === '' ? null : parseInt(e.target.value))}
                      placeholder="No limit"
                      className="mt-1"
                      data-testid={`tier-max-days-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Percentage (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={tier.percentage}
                      onChange={(e) => updateTier(index, 'percentage', parseFloat(e.target.value) || 0)}
                      className="mt-1"
                      data-testid={`tier-percentage-${index}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {tier.percentage >= 0 ? 'Positive = discount' : 'Negative = penalty'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            <Button
              variant="outline"
              onClick={addTier}
              className="w-full"
              data-testid="add-tier-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Tier
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Late Payment Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Late Payment Options
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Recurring Monthly Penalty</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, the late payment penalty is applied per month overdue.<br />
                Example: 2 months late at 1.5%/month = 3% total penalty
              </p>
            </div>
            <Switch
              checked={settings.late_penalty_recurring}
              onCheckedChange={(checked) => setSettings({ ...settings, late_penalty_recurring: checked })}
              data-testid="recurring-penalty-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>See how discounts/penalties will be applied</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Payment Timing</th>
                  <th className="text-right py-2 px-3">Rate</th>
                  <th className="text-right py-2 px-3">Example (R1,000 payment)</th>
                </tr>
              </thead>
              <tbody>
                {settings.tiers.map((tier, index) => {
                  const exampleAmount = 1000;
                  let adjustmentAmount, finalText;
                  
                  if (tier.percentage >= 0) {
                    adjustmentAmount = exampleAmount * (tier.percentage / 100);
                    finalText = `R${(exampleAmount - adjustmentAmount).toFixed(2)} (save R${adjustmentAmount.toFixed(2)})`;
                  } else {
                    adjustmentAmount = exampleAmount * (Math.abs(tier.percentage) / 100);
                    finalText = `R${(exampleAmount + adjustmentAmount).toFixed(2)} (+ R${adjustmentAmount.toFixed(2)} penalty)`;
                  }
                  
                  return (
                    <tr key={index} className="border-b">
                      <td className="py-2 px-3">{tier.label}</td>
                      <td className={`text-right py-2 px-3 font-medium ${tier.percentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tier.percentage >= 0 ? `-${tier.percentage}%` : `+${Math.abs(tier.percentage)}%`}
                      </td>
                      <td className="text-right py-2 px-3">{finalText}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountDiscountSettings;
